import { createFlag, updateFlag } from '@/data/services/flagServices';
import {
  createHackathon,
  HackathonDTO,
  updateHackathon,
} from '@/data/services/hackathonServices';
import { FlagTypeEnum } from '@/models/Flag';
import Hackathon from '@/models/Hackathon';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import HackathonForm, { HackathonFields } from './HackathonForm';
type HackathonSerciceProps = {
  hackathon?: Hackathon;
  disable?: boolean;
  onCancel?: () => void;
  onSucess?: (hackathon: Hackathon) => Promise<void>;
};
export default function HackathonService({
  hackathon,
  onSucess,
  onCancel,
  disable,
}: HackathonSerciceProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'hackathon.form.hackathon',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const handleCreate = async (hackathonFields: HackathonFields) => {
    const { title, edition } = hackathonFields;
    const flag = await createFlag({
      name: 'hackathon' + edition,
      type: FlagTypeEnum.GENERAL,
      userAssignable: true,
      description: title,
    });

    const hackathonPayload = {
      ...hackathonFields,
      flagId: flag.id,
    } as Omit<HackathonDTO, 'id'>;
    const newProject = await createHackathon(hackathonPayload);
    alert.success(t('successCreatedMessage'));
    return newProject;
  };

  const updateHackathonFlag = async ({ edition, title, flagId }: Hackathon) => {
    const flagFields = {
      name: 'hackathon' + edition,
      description: title,
    };

    return await updateFlag(flagId, flagFields);
  };

  const handleUpdate = async (hackathonFields: Partial<HackathonFields>) => {
    if (!hackathon) throw new Error('Hackathon not created');
    const { banner, ...fields } = hackathonFields;
    const hackathonChanges = buildChangedObject(
      hackathon,
      fields,
    ) as Partial<HackathonDTO>;
    const payload =
      banner === undefined ? hackathonChanges : { ...hackathonChanges, banner };
    const updatedHackathon = await updateHackathon(hackathon.id, payload);
    await updateHackathonFlag(updatedHackathon);
    alert.success(t('successUpdatedMessage'));
    return updatedHackathon;
  };

  const handleSubmit = (hackathonFields: HackathonFields) => {
    const response = !hackathon
      ? handleCreate(hackathonFields)
      : handleUpdate(hackathonFields);

    return response;
  };
  const { mutate: onSubmit, isPending: isLoading } = useMutation(handleSubmit, {
    onError: err => alert.error(getErrorMessage(err)),
    onSuccess: async project => {
      await onSucess?.(project);
      setHasChanges(false);
    },
  });
  return (
    <HackathonForm
      hackathon={hackathon}
      disable={disable}
      isLoading={isLoading}
      setHasChanges={setHasChanges}
      hasChanges={hasChanges}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  );
}
